import { Download, ExternalLink, File as FileIcon, FileImage, FileText, FileVideo } from 'lucide-react'
import * as React from 'react'

import { PdfViewer } from '@/components/pdf-viewer'
import { Button } from '@/components/ui/button'
import { PPTX_MIME } from '@/containers/resources/constants'
import { ImageContentViewer, PptxContentViewer } from '@/containers/resources/viewers'

import type { ResourceType } from '@/types/resource.type'

interface BlogFileViewerProps {
  fileName: string
  mimeType: string
  fileUrl: string
}

function toBlogResource(fileName: string, fileUrl: string): ResourceType {
  return {
    id: 0,
    uuid: '',
    uuidCopy: '',
    fileName,
    url: fileUrl,
    isProtected: false,
    isDownloadable: true,
    createdAt: '',
    updatedAt: '',
  }
}

function BlogFileViewer({ fileName, mimeType, fileUrl }: BlogFileViewerProps) {
  const resource = React.useMemo(() => toBlogResource(fileName, fileUrl), [fileName, fileUrl])

  if (mimeType.startsWith('image/')) {
    return (
      <div className="">
        <ImageContentViewer resource={resource} className="h-[100vh]" />
      </div>
    )
  }

  if (mimeType.includes('pdf')) {
    return (
      <div className="h-[100vh]">
        <PdfViewer sourceUrl={fileUrl} title={fileName} />
      </div>
    )
  }

  if (mimeType.includes(PPTX_MIME)) {
    return (
      <div className="h-[100vh]">
        <PptxContentViewer
          resource={resource}
          onLoadingComplete={async () => {
            // eslint-disable-next-line no-console
            console.log('onLoadingComplete')
          }}
          className="h-[90vh]"
        />
      </div>
    )
  }

  return <BlogGenericFileViewer fileName={fileName} mimeType={mimeType} fileUrl={fileUrl} />
}

function BlogGenericFileViewer({ fileName, mimeType, fileUrl }: BlogFileViewerProps) {
  return (
    <div className="pt-6 lg:pt-4">
      <div className="flex flex-col items-center justify-center rounded-xl border bg-muted/30 p-12 text-center gap-6">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background shadow-sm">
          {getFileIcon(mimeType)}
        </div>
        <div className="space-y-1.5">
          <h3 className="text-lg font-semibold">{fileName}</h3>
          <p className="text-sm text-muted-foreground">{mimeType}</p>
          <p className="text-sm text-muted-foreground">This file cannot be previewed in the browser.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="lg" className="gap-2" asChild>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="size-4" />
              Open
            </a>
          </Button>
          <Button size="lg" className="gap-2" asChild>
            <a href={fileUrl} download={fileName}>
              <Download className="size-4" />
              Download
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <FileImage className="size-8 text-blue-500" />
  if (mimeType.startsWith('video/')) return <FileVideo className="size-8 text-purple-500" />
  if (mimeType.includes('pdf') || mimeType.includes('text')) return <FileText className="size-8 text-red-500" />
  return <FileIcon className="size-8 text-muted-foreground" />
}

export default BlogFileViewer
